import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import useStore from "../../../../Store/Store";
import { SimulationsContext } from "./SimulationsTab";

// Constants for resize constraints
const MIN_HEIGHT_PERCENT = 3;
const MAX_HEIGHT_PERCENT = 97;
const HANDLE_HEIGHT = 20;

// New constants for the updated behavior
const EDITING_MIN_BOTTOM_CONTAINER_PERCENT = 35;
const EDITING_MAX_BOTTOM_CONTAINER_PERCENT = 80;
const DESCRIPTION_MIN_HEIGHT_PERCENT = 10;
const DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT = 30;
const CODE_MAX_HEIGHT_PERCENT = 70;

interface ResizeHandleProps {
  onResize: (newTopHeight: number) => void;
  topHeight: number; // Height of top section as percentage
  minTopHeight?: number;
  maxTopHeight?: number;
  className?: string;
}

// Generic reusable resize handle component
const ResizeHandle: React.FC<ResizeHandleProps> = ({
  onResize,
  topHeight,
  minTopHeight = MIN_HEIGHT_PERCENT,
  maxTopHeight = MAX_HEIGHT_PERCENT,
  className = "",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startMouseY = useRef<number>(0);
  const startTopHeight = useRef<number>(topHeight);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const container = containerRef.current.closest(
        ".simulationBody, .wikiAndCodeContainer"
      ) as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const deltaY = e.clientY - startMouseY.current;
      const containerHeight = containerRect.height;

      // Calculate new height based on the delta from start position
      let newTopHeight =
        startTopHeight.current + (deltaY / containerHeight) * 100;

      // Apply constraints
      newTopHeight = Math.max(
        minTopHeight,
        Math.min(maxTopHeight, newTopHeight)
      );

      onResize(newTopHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, onResize, minTopHeight, maxTopHeight]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startMouseY.current = e.clientY;
    startTopHeight.current = topHeight;
    setIsDragging(true);
  };

  return (
    <div
      ref={containerRef}
      className={`${className}`}
      style={{
        position: "absolute",
        top: `${topHeight}%`,
        height: `${HANDLE_HEIGHT}px`,
        width: "40%", // Fixed width to match CSS
        left: "50%",
        transform: "translateX(-50%)", // Center the handle
        cursor: "row-resize",
        borderRadius: "3px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "transparent",
        zIndex: 9999,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="verticalResizeHandle" />
    </div>
  );
};

interface ITopHandle {
  isDragging: React.MutableRefObject<boolean>;
  isMaxSD: boolean;
}

export const TopHandle: React.FC<ITopHandle> = ({ isDragging, isMaxSD }) => {
  const [topSectionHeight, setTopSectionHeight] = useState(40); // Changed from 60% to 40% for better initial proportions
  const isInitialized = useRef(false);

  const {
    currentUnsavedSimulationObject,
    isEditingSimulations,
    selectedSimulationKey,
    currentPath,
  } = useStore((state) => ({
    currentUnsavedSimulationObject: state.currentUnsavedSimulationObject,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    currentPath: state.currentPath,
  }));

  const { isRunning } = useContext(SimulationsContext);

  // Initialize from current DOM state with improved logic
  useEffect(() => {
    if (!isInitialized.current && selectedSimulationKey) {
      // Set CSS variable immediately to ensure consistency
      const root = document.documentElement;
      root.style.setProperty("--sim-files-height", "40%");

      const simStepsContainer = document.querySelector(
        ".SimStepsContainer"
      ) as HTMLElement;
      const container = document.querySelector(
        ".simulationBody"
      ) as HTMLElement;

      if (simStepsContainer && container) {
        const currentHeight = parseFloat(
          window.getComputedStyle(simStepsContainer).height
        );
        const containerHeight = container.getBoundingClientRect().height;
        const currentPercent = (currentHeight / containerHeight) * 100;

        // Only use current percentage if it's reasonable, otherwise use default
        if (
          !isNaN(currentPercent) &&
          currentPercent > 15 &&
          currentPercent < 85
        ) {
          setTopSectionHeight(currentPercent);
          root.style.setProperty("--sim-files-height", `${currentPercent}%`);
        } else {
          setTopSectionHeight(40);
          root.style.setProperty("--sim-files-height", "40%");
        }
        isInitialized.current = true;
      } else {
        // Fallback: set default values
        setTopSectionHeight(40);
        root.style.setProperty("--sim-files-height", "40%");
        isInitialized.current = true;
      }
    }
  }, [selectedSimulationKey]);

  // Auto-resize based on number of simulation steps and editing state
  useEffect(() => {
    if (isDragging.current) return; // Don't auto-resize while dragging

    const numberOfSimSteps = currentUnsavedSimulationObject.simSteps.length;
    const container = document.querySelector(".simulationBody");

    if (!container) return;

    const containerHeight = container.getBoundingClientRect().height;
    const handleHeightPercent = (HANDLE_HEIGHT / containerHeight) * 100;

    // Check if there's either code or step description
    const repoData = useStore.getState().repoData;
    const hasLinkedCode =
      repoData[currentPath]?.fileContent || repoData[currentPath]?.startLine;
    const hasDescription =
      currentUnsavedSimulationObject.description &&
      currentUnsavedSimulationObject.description.trim().length > 0;

    if (isRunning) {
      // When simulation is running: SimSteps gets 30%, description+code gets 70%
      setTopSectionHeight(22);
    } else if (isEditingSimulations) {
      // When in edit mode, default to 30% for SimSteps container
      setTopSectionHeight(30);
    } else {
      if (hasLinkedCode || hasDescription) {
        // When there's either code or step description: use 40%/60% split
        setTopSectionHeight(40);
      } else {
        // When not editing and no code/description: Position based on actual sim steps to avoid gaps
        const stepHeight = 41;
        const simStepsList = document.querySelector(".SimStepsList");

        let actualSimStepsHeight = stepHeight * numberOfSimSteps;

        if (simStepsList) {
          const scrollHeight = simStepsList.scrollHeight;
          actualSimStepsHeight = Math.min(scrollHeight, actualSimStepsHeight);
        }

        // Add some padding for headers and margins
        const paddingHeight = 60;
        const totalNeededHeight = actualSimStepsHeight + paddingHeight;
        const neededHeightPercent = (totalNeededHeight / containerHeight) * 100;

        // Ensure we don't go below minimum or above reasonable maximum
        const finalHeight = Math.max(15, Math.min(neededHeightPercent, 77));

        setTopSectionHeight(finalHeight);
      }
    }
  }, [
    currentUnsavedSimulationObject.simSteps.length,
    currentUnsavedSimulationObject.description,
    isRunning,
    isEditingSimulations,
    selectedSimulationKey,
    isMaxSD,
    isDragging,
    currentPath,
  ]);

  const handleResize = useCallback(
    (newHeight: number) => {
      setTopSectionHeight(newHeight);
      isDragging.current = true;

      // Update the CSS variable immediately
      const root = document.documentElement;
      root.style.setProperty("--sim-files-height", `${newHeight}%`);

      // Update the CSS for the containers
      const simStepsContainer = document.querySelector(
        ".SimStepsContainer"
      ) as HTMLElement;
      const wikiCodeContainer = document.querySelector(
        isEditingSimulations || isRunning
          ? ".wikiAndCodeContainer"
          : ".simulationDescriptionPreviewContainer"
      ) as HTMLElement;
      const container = document.querySelector(
        ".simulationBody"
      ) as HTMLElement;

      if (simStepsContainer) {
        simStepsContainer.style.height = `${newHeight}%`;
      }

      if (wikiCodeContainer && container) {
        // Calculate handle height as percentage of container
        const containerHeight = container.getBoundingClientRect().height;
        const handleHeightPercent = (HANDLE_HEIGHT / containerHeight) * 100;

        // Position the bottom container after the top section and handle
        const topPosition = newHeight + handleHeightPercent;
        const remainingHeight = 100 - newHeight - handleHeightPercent;

        wikiCodeContainer.style.position = "absolute";
        wikiCodeContainer.style.top = `${topPosition}%`;
        wikiCodeContainer.style.height = `${remainingHeight}%`;
        wikiCodeContainer.style.bottom = "auto";
        wikiCodeContainer.style.left = "0";
        wikiCodeContainer.style.right = "0";
        wikiCodeContainer.style.width = "100%";
      }
    },
    [isEditingSimulations, isRunning]
  );

  // Calculate dynamic constraints based on editing state
  const getConstraints = useCallback(() => {
    const container = document.querySelector(".simulationBody");
    if (!container) return { minTopHeight: 15, maxTopHeight: 77 };

    const containerHeight = container.getBoundingClientRect().height;
    const handleHeightPercent = (HANDLE_HEIGHT / containerHeight) * 100;

    if (isEditingSimulations || isRunning) {
      // When editing: bottom container must be between 35% and 80%
      const minTopHeight =
        100 - EDITING_MAX_BOTTOM_CONTAINER_PERCENT - handleHeightPercent;
      const maxTopHeight =
        100 - EDITING_MIN_BOTTOM_CONTAINER_PERCENT - handleHeightPercent;

      return {
        minTopHeight: Math.max(15, minTopHeight),
        maxTopHeight: Math.min(85, maxTopHeight),
      };
    } else {
      // When not editing: use standard constraints
      return {
        minTopHeight: 15,
        maxTopHeight: 77,
      };
    }
  }, [isEditingSimulations, isRunning]);

  // Cleanup dragging state
  useEffect(() => {
    const handleMouseUp = () => {
      isDragging.current = false;
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isDragging]);

  // Apply initial layout
  useEffect(() => {
    if (selectedSimulationKey) {
      const timeoutId = setTimeout(() => {
        handleResize(topSectionHeight);
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedSimulationKey, handleResize, topSectionHeight]);

  // Programmatic click fix for handle initialization when simulation starts running
  useEffect(() => {
    if (isRunning && selectedSimulationKey) {
      const timeoutId = setTimeout(() => {
        const codeWikiResizeHandle = document.querySelector(
          ".codeWikiResizeWrapper"
        ) as HTMLElement;
        if (codeWikiResizeHandle) {
          // Simulate a quick click to trigger the handle's resize logic
          const mouseDownEvent = new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            clientY: codeWikiResizeHandle.getBoundingClientRect().top,
          });
          const mouseUpEvent = new MouseEvent("mouseup", {
            bubbles: true,
            cancelable: true,
          });

          codeWikiResizeHandle.dispatchEvent(mouseDownEvent);
          // Immediately dispatch mouseup to complete the "click"
          setTimeout(
            () => codeWikiResizeHandle.dispatchEvent(mouseUpEvent),
            10
          );
        }
      }, 300); // Small delay to ensure DOM is ready

      return () => clearTimeout(timeoutId);
    }
  }, [isRunning, selectedSimulationKey]);

  // Additional effect to ensure layout is applied when editing state changes
  useEffect(() => {
    if (selectedSimulationKey && (isEditingSimulations || isRunning)) {
      const timeoutId = setTimeout(() => {
        handleResize(topSectionHeight);

        // Force update CSS variables to ensure consistency
        const root = document.documentElement;
        root.style.setProperty("--sim-files-height", `${topSectionHeight}%`);

        // Additional check to ensure the containers are properly positioned
        const wikiCodeContainer = document.querySelector(
          ".wikiAndCodeContainer"
        ) as HTMLElement;
        if (wikiCodeContainer) {
          // Force a repaint to ensure the layout is applied
          wikiCodeContainer.style.display = "none";
          const _ = wikiCodeContainer.offsetHeight; // Trigger reflow
          wikiCodeContainer.style.display = "flex";
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    isEditingSimulations,
    isRunning,
    selectedSimulationKey,
    handleResize,
    topSectionHeight,
  ]);

  if (isEditingSimulations === "Description") {
    return null;
  }

  const constraints = getConstraints();

  return (
    <ResizeHandle
      className="SimStepsAndEditorResizeWrapper"
      topHeight={topSectionHeight}
      onResize={handleResize}
      minTopHeight={constraints.minTopHeight}
      maxTopHeight={constraints.maxTopHeight}
    />
  );
};

interface IBottomHandle {
  simStepDescription: string;
}

export const BottomHandle: React.FC<IBottomHandle> = ({
  simStepDescription,
}) => {
  const [topSectionHeight, setTopSectionHeight] = useState(40); // Default 40%
  const { isRunning } = useContext(SimulationsContext);
  const isInitialized = useRef(false);

  const { isEditingSimulations, selectedSimulationKey, currentPath } = useStore(
    (state) => ({
      isEditingSimulations: state.simulationsState.isEditingSimulations,
      selectedSimulationKey: state.simulationsState.selectedSimulationKey,
      currentPath: state.currentPath,
    })
  );

  // Initialize from current DOM state
  useEffect(() => {
    if (!isInitialized.current && selectedSimulationKey) {
      const wikiContainer = document.querySelector(
        ".wikiContainer"
      ) as HTMLElement;
      const container = document.querySelector(
        ".wikiAndCodeContainer"
      ) as HTMLElement;

      if (wikiContainer && container) {
        const currentHeight = parseFloat(
          window.getComputedStyle(wikiContainer).height
        );
        const containerHeight = container.getBoundingClientRect().height;
        const currentPercent = (currentHeight / containerHeight) * 100;

        if (!isNaN(currentPercent) && currentPercent > 0) {
          setTopSectionHeight(currentPercent);
          isInitialized.current = true;
        }
      }
    }
  }, [selectedSimulationKey]);

  // Auto-resize based on content with new logic
  useEffect(() => {
    // Add small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      const repoData = useStore.getState().repoData;
      const hasLinkedCode =
        repoData[currentPath]?.fileContent || repoData[currentPath]?.startLine;
      const hasDescription =
        simStepDescription && simStepDescription.trim().length > 0;

      if (isEditingSimulations) {
        // When editing, use 50% split by default
        setTopSectionHeight(30);
        // Apply the resize immediately
        setTimeout(() => {
          handleResize(50);

          // Force content-based sizing to take effect
          const textEditorContainer = document.querySelector(
            ".textEditorContainer"
          ) as HTMLElement;
          const ckEditor = textEditorContainer?.querySelector(
            ".ck-editor__editable, .ck-content"
          ) as HTMLElement;

          if (ckEditor) {
            // Temporarily set height to auto to force content-based sizing
            ckEditor.style.height = "auto";
            void ckEditor.offsetHeight; // Force reflow

            // The CSS min/max heights will constrain it appropriately
          }
        }, 50);
        return;
      }

      // New logic: Check if source code window has code content
      const hasCodeContent =
        hasLinkedCode &&
        repoData[currentPath]?.fileContent &&
        repoData[currentPath].fileContent.trim().length > 0;

      // Helper function to measure actual text editor content height with improved accuracy
      const measureTextEditorHeight = (): number => {
        const wikiContainer = document.querySelector(".wikiContainer");
        const textEditorContainer = wikiContainer?.querySelector(
          ".textEditorContainer"
        ) as HTMLElement;

        if (!textEditorContainer || !wikiContainer) {
          return 0;
        }

        // Find the CKEditor content element
        const ckEditor = textEditorContainer.querySelector(
          ".ck-editor__editable, .ck-content"
        ) as HTMLElement;

        if (!ckEditor) {
          return 60; // Return minimal height if no editor found
        }

        // Check if content is empty
        const textContent = ckEditor.textContent?.trim() || "";
        const htmlContent = ckEditor.innerHTML?.trim() || "";

        if (
          textContent.length === 0 ||
          htmlContent === '<p><br data-cke-filler="true"></p>' ||
          htmlContent === "<p></p>"
        ) {
          return 60; // Minimal height for empty content
        }

        // Create a temporary element to measure the actual content height
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.visibility = "hidden";
        tempDiv.style.width = `${ckEditor.clientWidth}px`;
        tempDiv.style.fontSize = window.getComputedStyle(ckEditor).fontSize;
        tempDiv.style.fontFamily = window.getComputedStyle(ckEditor).fontFamily;
        tempDiv.style.lineHeight = window.getComputedStyle(ckEditor).lineHeight;
        tempDiv.style.padding = window.getComputedStyle(ckEditor).padding;
        tempDiv.innerHTML = ckEditor.innerHTML;

        document.body.appendChild(tempDiv);
        const actualContentHeight = tempDiv.scrollHeight;
        document.body.removeChild(tempDiv);

        // Add UI elements height
        const hasDataButtons = !!document.querySelector(
          '.wikiContainer [style*="45px"], .wikiContainer .DataExampleButtons'
        );
        const dataButtonsHeight = hasDataButtons ? 45 : 0;
        const toolbarHeight = 50; // CKEditor toolbar height (positioned at top: 50px)
        const containerPadding = 20; // Top padding and margins

        const totalHeight =
          actualContentHeight +
          dataButtonsHeight +
          toolbarHeight +
          containerPadding;

        // Debug logging
        console.log("Content measurement (improved):", {
          textLength: textContent.length,
          actualContentHeight,
          hasDataButtons,
          totalHeight,
        });

        return Math.max(totalHeight, 80); // Ensure reasonable minimum
      };

      if (hasCodeContent && hasDescription) {
        // Both step description and code content present:
        // Auto-size description based on content with tighter constraints
        const container = document.querySelector(
          ".wikiAndCodeContainer"
        ) as HTMLElement;
        if (!container) return;

        const containerHeight = container.clientHeight;
        const measuredHeight = measureTextEditorHeight();
        let wikiHeightPercent = (measuredHeight * 100) / containerHeight;

        // More precise sizing: if content is small, use smaller height
        let maxHeight = 50;
        if (wikiHeightPercent < 25) {
          maxHeight = Math.max(wikiHeightPercent + 5, 20); // Add small buffer
        }

        const finalHeight = Math.min(
          Math.max(wikiHeightPercent, 15),
          maxHeight
        );
        setTopSectionHeight(finalHeight);
        // Apply the resize immediately to fix initialization bug
        setTimeout(() => {
          handleResize(finalHeight);

          // Force content-based sizing to take effect
          const textEditorContainer = document.querySelector(
            ".textEditorContainer"
          ) as HTMLElement;
          const ckEditor = textEditorContainer?.querySelector(
            ".ck-editor__editable, .ck-content"
          ) as HTMLElement;

          if (ckEditor) {
            // Temporarily set height to auto to force content-based sizing
            ckEditor.style.height = "auto";
            void ckEditor.offsetHeight; // Force reflow

            // The CSS min/max heights will constrain it appropriately
          }
        }, 50);
        return;
      }

      if (hasDescription) {
        // Step description present: auto-size with tighter constraints
        const container = document.querySelector(
          ".wikiAndCodeContainer"
        ) as HTMLElement;
        if (!container) {
          setTopSectionHeight(DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT);
          setTimeout(
            () => handleResize(DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT),
            50
          );
          return;
        }

        const containerHeight = container.clientHeight;
        const measuredHeight = measureTextEditorHeight();
        let wikiHeightPercent = (measuredHeight * 100) / containerHeight;

        // More precise sizing: if content is small, use smaller height
        let maxHeight = 50;
        if (wikiHeightPercent < 30) {
          maxHeight = Math.max(wikiHeightPercent + 5, 25); // Add small buffer
        }

        const finalHeight = Math.min(
          Math.max(wikiHeightPercent, DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT),
          maxHeight
        );
        setTopSectionHeight(finalHeight);
        // Apply the resize immediately to fix initialization bug
        setTimeout(() => {
          handleResize(finalHeight);

          // Force content-based sizing to take effect
          const textEditorContainer = document.querySelector(
            ".textEditorContainer"
          ) as HTMLElement;
          const ckEditor = textEditorContainer?.querySelector(
            ".ck-editor__editable, .ck-content"
          ) as HTMLElement;

          if (ckEditor) {
            // Temporarily set height to auto to force content-based sizing
            ckEditor.style.height = "auto";
            void ckEditor.offsetHeight; // Force reflow

            // The CSS min/max heights will constrain it appropriately
          }
        }, 50);
        return;
      }

      if (!hasDescription) {
        // No description: use minimal height (10%)
        setTopSectionHeight(DESCRIPTION_MIN_HEIGHT_PERCENT);
        setTimeout(() => handleResize(DESCRIPTION_MIN_HEIGHT_PERCENT), 50);
        return;
      }

      // Fallback: has description, calculate content-based height
      const container = document.querySelector(
        ".wikiAndCodeContainer"
      ) as HTMLElement;
      if (!container) {
        setTopSectionHeight(DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT);
        setTimeout(
          () => handleResize(DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT),
          50
        );
        return;
      }

      const containerHeight = container.clientHeight;
      const measuredHeight = measureTextEditorHeight();
      let wikiHeightPercent = (measuredHeight * 100) / containerHeight;

      // More precise sizing with tighter constraints
      let maxHeight = 50;
      if (wikiHeightPercent < 30) {
        maxHeight = Math.max(wikiHeightPercent + 5, 25); // Add small buffer
      }

      const finalHeight = Math.min(
        Math.max(wikiHeightPercent, DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT),
        maxHeight
      );

      setTopSectionHeight(finalHeight);
      // Apply the resize immediately to fix initialization bug
      setTimeout(() => {
        handleResize(finalHeight);

        // Force content-based sizing to take effect
        const textEditorContainer = document.querySelector(
          ".textEditorContainer"
        ) as HTMLElement;
        const ckEditor = textEditorContainer?.querySelector(
          ".ck-editor__editable, .ck-content"
        ) as HTMLElement;

        if (ckEditor) {
          // Temporarily set height to auto to force content-based sizing
          ckEditor.style.height = "auto";
          void ckEditor.offsetHeight; // Force reflow

          // The CSS min/max heights will constrain it appropriately
        }
      }, 50);
    }, 500); // Increased delay to ensure editor is fully rendered

    return () => clearTimeout(timeoutId);
  }, [
    simStepDescription,
    isEditingSimulations,
    isRunning,
    currentPath,
    selectedSimulationKey,
  ]);

  const handleResize = useCallback((newHeight: number) => {
    setTopSectionHeight(newHeight);

    const wikiContainer = document.querySelector(
      ".wikiContainer"
    ) as HTMLElement;
    const codeContainer = document.querySelector(
      ".simulationCodeContainer"
    ) as HTMLElement;
    const container = document.querySelector(
      ".wikiAndCodeContainer"
    ) as HTMLElement;

    if (wikiContainer) {
      wikiContainer.style.height = `${newHeight}%`;
    }

    if (codeContainer && container) {
      // Calculate handle height as percentage
      const containerHeight = container.getBoundingClientRect().height;
      const handleHeightPercent = (HANDLE_HEIGHT / containerHeight) * 100;

      // Position code container after wiki and handle
      const topPosition = newHeight + handleHeightPercent;
      const remainingHeight = 100 - newHeight - handleHeightPercent;

      // Account for the boxTitle positioning and ensure we don't crop the bottom
      // The boxTitle has a negative top margin, so we need to add some padding
      const titleOffset = 0.5; // Small offset to account for title positioning
      const adjustedRemainingHeight = Math.max(
        remainingHeight - titleOffset,
        10
      ); // Ensure minimum height

      codeContainer.style.position = "absolute";
      codeContainer.style.top = `${topPosition}%`;
      codeContainer.style.height = `${adjustedRemainingHeight}%`;
      codeContainer.style.bottom = "auto";
      codeContainer.style.left = "0";
      codeContainer.style.right = "0";
      codeContainer.style.width = "100%";

      // Ensure the container doesn't overflow
      codeContainer.style.maxHeight = `calc(100% - ${topPosition}% - 10px)`;
    }
  }, []);

  // Calculate dynamic constraints for bottom handle
  const getBottomConstraints = useCallback(() => {
    const repoData = useStore.getState().repoData;
    const hasLinkedCode =
      repoData[currentPath]?.fileContent || repoData[currentPath]?.startLine;
    const hasDescription =
      simStepDescription && simStepDescription.trim().length > 0;
    const hasCodeContent =
      hasLinkedCode &&
      repoData[currentPath]?.fileContent &&
      repoData[currentPath].fileContent.trim().length > 0;

    if (hasCodeContent && hasDescription) {
      // Both step description and code content present:
      // Allow manual dragging up to 80%, code gets minimum 20%
      return {
        minTopHeight: DESCRIPTION_MIN_HEIGHT_PERCENT,
        maxTopHeight: 80, // Manual dragging can go up to 80%
      };
    }

    if (hasDescription) {
      // Step description present: allow manual dragging up to 80%
      return {
        minTopHeight: DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT,
        maxTopHeight: 80, // Manual dragging can go up to 80%
      };
    }

    if (!hasDescription) {
      // No description: minimal range around 10%
      return {
        minTopHeight: DESCRIPTION_MIN_HEIGHT_PERCENT - 2,
        maxTopHeight: DESCRIPTION_MIN_HEIGHT_PERCENT + 5,
      };
    }

    // Fallback
    return {
      minTopHeight: DESCRIPTION_MIN_CONTENT_HEIGHT_PERCENT,
      maxTopHeight: 80, // Manual dragging can go up to 80%
    };
  }, [simStepDescription, currentPath]);

  // Apply initial layout
  useEffect(() => {
    if (selectedSimulationKey) {
      const timeoutId = setTimeout(() => {
        handleResize(topSectionHeight);
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedSimulationKey, handleResize, topSectionHeight]);

  // Programmatic click fix for handle initialization when simulation starts running
  useEffect(() => {
    if (isRunning && selectedSimulationKey) {
      const timeoutId = setTimeout(() => {
        const codeWikiResizeHandle = document.querySelector(
          ".codeWikiResizeWrapper"
        ) as HTMLElement;
        if (codeWikiResizeHandle) {
          // Simulate a quick click to trigger the handle's resize logic
          const mouseDownEvent = new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            clientY: codeWikiResizeHandle.getBoundingClientRect().top,
          });
          const mouseUpEvent = new MouseEvent("mouseup", {
            bubbles: true,
            cancelable: true,
          });

          codeWikiResizeHandle.dispatchEvent(mouseDownEvent);
          // Immediately dispatch mouseup to complete the "click"
          setTimeout(
            () => codeWikiResizeHandle.dispatchEvent(mouseUpEvent),
            10
          );
        }
      }, 300); // Small delay to ensure DOM is ready

      return () => clearTimeout(timeoutId);
    }
  }, [isRunning, selectedSimulationKey]);

  // Additional effect to ensure layout is applied when editing state changes
  useEffect(() => {
    if (selectedSimulationKey && isEditingSimulations) {
      const timeoutId = setTimeout(() => {
        handleResize(topSectionHeight);

        // Force update CSS variables to ensure consistency
        const root = document.documentElement;
        root.style.setProperty(
          "--wiki-container-height",
          `${topSectionHeight}%`
        );

        // Additional check to ensure the code container is properly positioned
        const codeContainer = document.querySelector(
          ".simulationCodeContainer"
        ) as HTMLElement;
        if (codeContainer) {
          // Force a repaint to ensure the layout is applied
          codeContainer.style.display = "none";
          const _ = codeContainer.offsetHeight; // Trigger reflow
          codeContainer.style.display = "block";
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    isEditingSimulations,
    selectedSimulationKey,
    handleResize,
    topSectionHeight,
  ]);

  const constraints = getBottomConstraints();

  return (
    <ResizeHandle
      className="codeWikiResizeWrapper"
      topHeight={topSectionHeight}
      onResize={handleResize}
      minTopHeight={constraints.minTopHeight}
      maxTopHeight={constraints.maxTopHeight}
    />
  );
};
